import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Auth0Session,
  DestinationGate,
  DevelopmentSession,
  LoginShell,
  PRODUCT_TAGLINES,
  SigningIn,
  signingInTitle,
  ProductLockup,
  StatusView,
  type DestinationClient,
  type DestinationSession,
} from '@traceio-org/auth-ui';
import { createPlatformClient, type PlatformClient } from '@traceio-org/platform-client';
import { DEFAULT_PLATFORM_API_AUDIENCE, PRODUCTS, type ProductKey } from '@traceio-org/platform-contracts';
import type { FixtureConfig } from './config';
import { FixturePage } from './FixturePage';
import { checkFixtureRegistration } from './registry';

/** Everything the fixture talks to; tests replace any part. */
export interface FixtureDependencies {
  client: DestinationClient & Pick<PlatformClient, 'applications'>;
  /** The session for the product's registry audience: Auth0 when configured, else development identity. */
  createSession: (audience: string) => DestinationSession;
  /** This page's origin, checked against the registry. */
  origin: string;
  /** Full-page navigation used by the gate (the browser's by default). */
  assign?: (url: string) => void;
}

export function createFixtureDependencies(config: FixtureConfig): FixtureDependencies {
  // Tokens are passed per call by the gate, so the client needs no token source of its own.
  const client = createPlatformClient({ baseUrl: config.platformApiUrl });
  return {
    client,
    origin: window.location.origin,
    createSession: (audience) =>
      config.auth0
        ? new Auth0Session({ domain: config.auth0.domain, clientId: config.auth0.clientId, audience })
        : // Development identity: the session the hub started, shared through platform-api's cookie.
          new DevelopmentSession({ client, defaultAudience: audience }),
  };
}

function useHeading(title: string) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    document.title = `${title} | Trace`;
    ref.current?.focus({ preventScroll: true });
  }, [title]);
  return ref;
}

function Frame({ productKey, children }: { productKey: ProductKey | null; children: ReactNode }) {
  return (
    <LoginShell
      brand={
        productKey ? (
          <ProductLockup productKey={productKey} surface="dark" />
        ) : (
          <ProductLockup lockup="trace" surface="dark" />
        )
      }
      tagline={productKey ? PRODUCT_TAGLINES[productKey] : undefined}
    >
      {children}
    </LoginShell>
  );
}

/** Rendered instead of the fixture when it must not run: the gate never starts. */
export function RefusedPage({ productKey, errors }: { productKey: ProductKey | null; errors: string[] }) {
  const title = 'This navigation fixture cannot start';
  const headingRef = useHeading(title);
  return (
    <Frame productKey={productKey}>
      <StatusView tone="error" title={title} headingRef={headingRef}>
        <p>
          It is a local development stand-in for a Trace product, and it refuses to run with these settings:
        </p>
        <ul className="trace-auth-config-errors">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </StatusView>
    </Frame>
  );
}

function Loading({ productKey }: { productKey: ProductKey }) {
  // The first screen of a sign-in: the same heading as the static shell before it and DestinationGate
  // after it (SigningIn), so loading the registry does not add a heading of its own between them.
  const title = signingInTitle(PRODUCTS[productKey].displayName);
  const headingRef = useHeading(title);
  return (
    <Frame productKey={productKey}>
      <SigningIn title={title} headingRef={headingRef} />
    </Frame>
  );
}

function RegistryFailed({ productKey, onRetry }: { productKey: ProductKey; onRetry: () => void }) {
  const title = 'We could not load the Trace application registry';
  const headingRef = useHeading(title);
  return (
    <Frame productKey={productKey}>
      <StatusView
        tone="error"
        title={title}
        headingRef={headingRef}
        primaryAction={{ label: 'Try again', onClick: onRetry }}
      >
        <p>
          This fixture checks that platform-api registers it before it opens. Check that platform-api is
          running, then try again.
        </p>
      </StatusView>
    </Frame>
  );
}

type Boot =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'refused'; errors: string[] }
  | { kind: 'ready'; audience: string; session: DestinationSession };

/**
 * The fixture: it loads the registry, refuses to start unless the registry marks this origin as the
 * product's navigation fixture in this environment, then wraps the page in DestinationGate with the
 * product's registry audience. Nothing about the person is shown before the gate confirmed access.
 */
export function FixtureApp({ config, deps }: { config: FixtureConfig; deps: FixtureDependencies }) {
  const [boot, setBoot] = useState<Boot>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    deps.client.applications().then(
      (registry) => {
        if (!active) return;
        const registration = checkFixtureRegistration(registry, config, deps.origin);
        // A fixture has no backend: its only API is platform-api (navigation decide, me/access), so it signs in
        // for the platform audience and that one token serves the gate. With the product's own audience the gate
        // needed a second, platform token, which auth0-spa-js cannot refresh for another audience
        // (missing_refresh_token): "We could not check your access" on hosted dev, 2026-10-07. The registry
        // check above still requires this origin to be the product's fixture.
        setBoot(
          registration.ok
            ? {
                kind: 'ready',
                audience: DEFAULT_PLATFORM_API_AUDIENCE,
                session: deps.createSession(DEFAULT_PLATFORM_API_AUDIENCE),
              }
            : { kind: 'refused', errors: registration.errors },
        );
      },
      (error: unknown) => {
        if (!active) return;
        console.warn('dev-destinations: application registry unavailable', error);
        setBoot({ kind: 'failed' });
      },
    );
    return () => {
      active = false;
    };
  }, [config, deps, attempt]);

  switch (boot.kind) {
    case 'loading':
      return <Loading productKey={config.productKey} />;
    case 'failed':
      return (
        <RegistryFailed
          productKey={config.productKey}
          onRetry={() => {
            setBoot({ kind: 'loading' });
            setAttempt((n) => n + 1);
          }}
        />
      );
    case 'refused':
      return <RefusedPage productKey={config.productKey} errors={boot.errors} />;
    case 'ready':
      return (
        <DestinationGate
          productKey={config.productKey}
          session={boot.session}
          client={deps.client}
          hubOrigin={config.hubOrigin}
          audience={boot.audience}
          {...(deps.assign ? { assign: deps.assign } : {})}
        >
          <FixturePage hubOrigin={config.hubOrigin} hostedPreview={config.hostedPreview} />
        </DestinationGate>
      );
  }
}
